// ---------------------------------------------------------------------------
// Loyalty — แต้มสะสม / ระดับสมาชิก / แลกของรางวัล
// ---------------------------------------------------------------------------
// ทุกฟังก์ชันรับ orgId และอ่าน/เขียนผ่าน repository เท่านั้น (ข้อมูลผูกร้านเสมอ)
// ยอดแต้มจริงของลูกค้าเก็บ 2 ที่ให้ตรงกัน: customers.points และ wallets.points
//
// earnPoints() ออกแบบให้ขั้นตอนชำระเงิน (เช่น settlement) เรียกหลังปิดบิลสำเร็จ
// ---------------------------------------------------------------------------
import { customerRepo, walletRepo, pointRepo, tierRepo, rewardRepo } from "../mock/repositories";

export class LoyaltyError extends Error {
  constructor(message, code = "invalid") {
    super(message);
    this.name = "LoyaltyError";
    this.code = code;
  }
}

const num = (value) => Number(value) || 0;

/** เรียงระดับจากเกณฑ์ต่ำไปสูง */
export const sortTiers = (tiers = []) => [...tiers].sort((a, b) => num(a.minSpend) - num(b.minSpend));

/** ระดับที่ตรงกับยอดใช้จ่ายสะสม (ระดับสูงสุดที่ minSpend <= spend) */
export const tierForSpend = (tiers = [], spend = 0) => {
  const sorted = sortTiers(tiers);
  let current = sorted[0] || null;
  sorted.forEach((tier) => {
    if (num(tier.minSpend) <= num(spend)) current = tier;
  });
  return current;
};

/** ระดับถัดไป (เกณฑ์สูงกว่ายอดปัจจุบัน) — null ถ้าถึงระดับสูงสุดแล้ว */
export const nextTierFor = (tiers = [], spend = 0) => sortTiers(tiers).find((tier) => num(tier.minSpend) > num(spend)) || null;

const findWallet = async (orgId, customerId) => (await walletRepo.all(orgId, (row) => row.customerId === customerId))[0] || null;

const loadCustomer = async (orgId, customerId) => {
  const customer = customerId ? await customerRepo.get(orgId, customerId) : null;
  // ต้องเป็นลูกค้าของร้านนี้จริง (กันเดา id ข้ามร้าน)
  if (!customer || customer.id !== customerId) throw new LoyaltyError("ไม่พบลูกค้าในร้านนี้", "not_found");
  return customer;
};

/** ยอดแต้มปัจจุบัน — ใช้ wallet ถ้ามี ไม่งั้นใช้ customers.points */
export const pointBalance = (customer, wallet) => num(wallet ? wallet.points : customer?.points);

/** เขียนยอดแต้มใหม่ลงทั้ง customers และ wallets (สร้าง wallet ให้ถ้ายังไม่มี) */
const writeBalance = async (orgId, customer, wallet, balance, extra = {}) => {
  await customerRepo.update(orgId, customer.id, { points: balance, ...extra });
  if (wallet) return walletRepo.update(orgId, wallet.id, { points: balance });
  return walletRepo.create(orgId, { customerId: customer.id, balance: 0, points: balance, coupons: [] });
};

/**
 * ปรับแต้มด้วยมือ (+ ให้ / − หัก) พร้อมบันทึกประวัติ
 * points ส่งเป็นจำนวนเต็มบวกเสมอ ทิศทางดูจาก direction = "earn" | "deduct"
 */
export const adjustPoints = async ({ orgId, customerId, direction, points, detail }) => {
  if (!orgId) throw new LoyaltyError("ไม่พบร้าน", "no_org");
  const amount = Math.floor(num(points));
  if (!(amount > 0)) throw new LoyaltyError("จำนวนแต้มต้องมากกว่า 0", "bad_points");
  if (direction !== "earn" && direction !== "deduct") throw new LoyaltyError("ประเภทรายการไม่ถูกต้อง", "bad_type");

  const customer = await loadCustomer(orgId, customerId);
  const wallet = await findWallet(orgId, customerId);
  const balance = pointBalance(customer, wallet);
  const delta = direction === "earn" ? amount : -amount;
  if (balance + delta < 0) throw new LoyaltyError("แต้มของลูกค้าไม่พอให้หัก", "insufficient");

  const tx = await pointRepo.create(orgId, {
    customerId,
    customerName: customer.name,
    type: direction === "earn" ? "earn" : "adjust",
    points: delta,
    detail: detail || (direction === "earn" ? "แอดมินเพิ่มแต้มให้" : "แอดมินหักแต้ม"),
    createdAt: new Date().toISOString(),
  });
  await writeBalance(orgId, customer, wallet, balance + delta);
  return { transaction: tx, balance: balance + delta };
};

/**
 * สะสมแต้มจากยอดชำระ — ให้ขั้นตอนชำระเงินเรียก
 *   แต้ม = floor(amount x pointRate ของระดับปัจจุบัน)
 * พร้อมบวก totalSpend / visits และปรับระดับสมาชิกตามเกณฑ์ใหม่
 * ไม่มีลูกค้า (เช่นขายหน้าร้านไม่ระบุสมาชิก) หรือยอด <= 0 -> คืน null ไม่ทำอะไร
 */
export const earnPoints = async ({ orgId, customerId, amount, detail, refNo, transactionId }) => {
  const paid = num(amount);
  if (!orgId || !customerId || !(paid > 0)) return null;

  // กันได้แต้มซ้ำต่อธุรกรรมเดียวกัน (เรียกซ้ำ/พยายามบันทึกใหม่)
  if (transactionId) {
    const done = (await pointRepo.all(orgId, (row) => row.type === "earn" && row.transactionId === transactionId))[0];
    if (done) return { transaction: done, earned: 0, duplicate: true };
  }

  const customer = await loadCustomer(orgId, customerId);
  const wallet = await findWallet(orgId, customerId);
  const tiers = await tierRepo.all(orgId);

  const currentTier = tiers.find((tier) => tier.key === customer.tier) || tierForSpend(tiers, customer.totalSpend);
  const earned = Math.floor(paid * (num(currentTier?.pointRate) || 1));
  const totalSpend = num(customer.totalSpend) + paid;
  const newTier = tierForSpend(tiers, totalSpend);
  const balance = pointBalance(customer, wallet) + earned;

  const tx = earned > 0
    ? await pointRepo.create(orgId, {
        customerId,
        customerName: customer.name,
        type: "earn",
        points: earned,
        detail: detail || (refNo ? `สะสมจากการชำระ ${refNo}` : "สะสมจากการใช้บริการ"),
        refNo: refNo || "",
        transactionId: transactionId || "",
        createdAt: new Date().toISOString(),
      })
    : null;
  await writeBalance(orgId, customer, wallet, balance, {
    totalSpend,
    visits: num(customer.visits) + 1,
    lastVisitAt: new Date().toISOString(),
    ...(newTier ? { tier: newTier.key } : {}),
  });
  return { transaction: tx, earned, balance, tier: newTier?.key || customer.tier };
};

const COUPON_VALID_DAYS = 90;

/**
 * แลกของรางวัล: เช็คแต้ม/สต็อกจากข้อมูลล่าสุด -> บันทึกประวัติ -> หักแต้ม -> ตัดสต็อก -> ใส่คูปองลงกระเป๋า
 */
export const redeemReward = async ({ orgId, customerId, rewardId }) => {
  if (!orgId) throw new LoyaltyError("ไม่พบร้าน", "no_org");
  const customer = await loadCustomer(orgId, customerId);
  const reward = rewardId ? await rewardRepo.get(orgId, rewardId) : null;
  if (!reward || reward.id !== rewardId) throw new LoyaltyError("ไม่พบของรางวัลนี้", "not_found");

  const cost = Math.floor(num(reward.points));
  if (!(cost > 0)) throw new LoyaltyError("ของรางวัลนี้ยังไม่ได้ตั้งแต้มที่ใช้แลก", "bad_reward");
  if (num(reward.stock) < 1) throw new LoyaltyError("ของรางวัลหมดแล้ว", "out_of_stock");

  const wallet = await findWallet(orgId, customerId);
  const balance = pointBalance(customer, wallet);
  if (balance < cost) throw new LoyaltyError("แต้มไม่พอสำหรับแลกของรางวัลนี้", "insufficient");

  const now = new Date();
  const tx = await pointRepo.create(orgId, {
    customerId,
    customerName: customer.name,
    type: "redeem",
    points: -cost,
    detail: `แลก ${reward.name}`,
    rewardId: reward.id,
    createdAt: now.toISOString(),
  });

  const coupon = {
    id: `cpn_${now.getTime().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`,
    name: reward.name,
    rewardId: reward.id,
    expireAt: new Date(now.getTime() + COUPON_VALID_DAYS * 86400000).toISOString(),
  };
  const coupons = [...(Array.isArray(wallet?.coupons) ? wallet.coupons : []), coupon];
  const newBalance = balance - cost;

  await customerRepo.update(orgId, customer.id, { points: newBalance });
  if (wallet) await walletRepo.update(orgId, wallet.id, { points: newBalance, coupons });
  else await walletRepo.create(orgId, { customerId, balance: 0, points: newBalance, coupons });
  await rewardRepo.update(orgId, reward.id, { stock: num(reward.stock) - 1 });

  return { transaction: tx, coupon, balance: newBalance };
};
